/** Fixed dial priority order — ticket #1823's full ladder before WAN fallback. */
const DIRECT_TIER_ORDER = ["lan-direct", "lan-webtransport", "wan-direct"];
/**
 * Direct (non-relayed) home-node transport policy: browser-trusted LAN
 * HTTPS, then hash-pinned LAN WebTransport, then WAN HTTPS/WSS (ticket
 * #1823). Adapters own I/O; this class owns selection only.
 */
export class TransportManager {
    #candidates;
    #dial;
    #delay;
    #candidateTimeoutMs;
    #now;
    #onState;
    #run;
    #runAbort;
    #selectedCandidate;
    #controllers = new Set();
    #closedClients = new WeakSet();
    #listeners = new Set();
    #state = { changedAt: "1970-01-01T00:00:00.000Z", path: "offline", status: "offline" };
    constructor(options) {
        this.#candidates = normalizeDirectCandidates(options.candidates);
        this.#dial = options.dial;
        this.#delay = options.delay;
        this.#candidateTimeoutMs = Math.max(1, options.candidateTimeoutMs ?? 15_000);
        this.#now = options.now ?? (() => new Date().toISOString());
        this.#onState = options.onState ?? (() => undefined);
    }
    get state() { return this.#state; }
    get selectedCandidate() {
        return this.#selectedCandidate === undefined ? undefined : { ...this.#selectedCandidate };
    }
    subscribe(listener) {
        this.#listeners.add(listener);
        return () => this.#listeners.delete(listener);
    }
    replaceCandidates(candidates) {
        this.#candidates = normalizeDirectCandidates(candidates);
    }
    connect(options = {}) {
        if (options.higherPriorityThan !== undefined && options.path !== undefined) {
            throw new TypeError("Transport selection accepts either a path or a priority bound");
        }
        const candidates = options.path === undefined
            ? options.higherPriorityThan === undefined
                ? this.#candidates
                : this.#candidates.filter((candidate) => priority(candidate.path) < priority(options.higherPriorityThan))
            : this.#candidates.filter((candidate) => candidate.path === options.path);
        return this.#shareRun(candidates, options.higherPriorityThan === undefined);
    }
    markConnected(path) {
        this.#setState({ changedAt: this.#now(), path, status: "connected" });
    }
    markOffline() {
        this.#setState({ changedAt: this.#now(), path: "offline", status: "offline" });
    }
    abort() {
        this.#runAbort?.abort();
        for (const controller of this.#controllers)
            controller.abort();
        this.markOffline();
    }
    #shareRun(candidates, publishState) {
        if (this.#run !== undefined)
            return this.#run;
        const abort = new AbortController();
        const connection = this.#connect(candidates, publishState, abort.signal);
        const run = connection.finally(() => {
            if (this.#run === run)
                this.#run = undefined;
            if (this.#runAbort === abort)
                this.#runAbort = undefined;
        });
        this.#run = run;
        this.#runAbort = abort;
        return run;
    }
    async #connect(candidates, publishState, signal) {
        const failures = [];
        for (const path of DIRECT_TIER_ORDER) {
            throwIfAborted(signal);
            const tier = candidates.filter((candidate) => candidate.path === path);
            if (tier.length === 0)
                continue;
            const attempts = tier.map((candidate) => this.#startAttempt(candidate, signal));
            const outcomes = await Promise.all(attempts.map(async (attempt) => {
                try {
                    return { attempt, client: await attempt.promise, success: true };
                }
                catch (error) {
                    failures.push(`${attempt.candidate.path}:${messageFor(error)}`);
                    return { attempt, success: false };
                }
                finally {
                    this.#controllers.delete(attempt.controller);
                }
            }));
            throwIfAborted(signal);
            const successes = outcomes.filter((outcome) => outcome.success);
            const winner = successes[0];
            if (winner === undefined) {
                for (const attempt of attempts)
                    attempt.controller.abort();
                continue;
            }
            await Promise.all(successes.slice(1).map(async (outcome) => {
                outcome.attempt.controller.abort();
                await this.#closeClient(outcome.client);
            }));
            this.#selectedCandidate = { ...winner.attempt.candidate };
            if (publishState)
                this.markConnected(winner.attempt.candidate.path);
            return winner.client;
        }
        if (publishState)
            this.markOffline();
        throw new Error(`No home-node transport available (${failures.join(", ") || "no candidates"})`);
    }
    #startAttempt(candidate, runSignal) {
        const controller = new AbortController();
        this.#controllers.add(controller);
        return {
            candidate,
            controller,
            promise: this.#dialWithTimeout(candidate, controller, runSignal),
        };
    }
    async #dialWithTimeout(candidate, controller, runSignal) {
        const dial = this.#dial(candidate, controller.signal);
        void dial.then(async (client) => {
            if (controller.signal.aborted || runSignal.aborted)
                await this.#closeClient(client);
        }).catch(() => undefined);
        const cancellation = Promise.race([rejectedOnAbort(controller.signal), rejectedOnAbort(runSignal)]);
        if (this.#delay === undefined)
            return await Promise.race([dial, cancellation]);
        const timeout = this.#delay.wait(this.#candidateTimeoutMs).then(() => {
            controller.abort();
            throw new Error("candidate dial timed out");
        });
        return await Promise.race([dial, timeout, cancellation]);
    }
    async #closeClient(client) {
        if (this.#closedClients.has(client))
            return;
        this.#closedClients.add(client);
        await closeClient(client);
    }
    #setState(next) {
        if (this.#state.status === next.status && this.#state.path === next.path)
            return;
        this.#state = next;
        this.#onState(next);
        for (const listener of [...this.#listeners])
            listener(next);
    }
}
function normalizeDirectCandidates(candidates) {
    const tiers = {
        "lan-direct": [],
        "lan-webtransport": [],
        "wan-direct": [],
    };
    const seen = new Set();
    const trustedNodeIds = new Set();
    for (const candidate of candidates) {
        if (!isDirectCandidate(candidate) || candidate.address.trim() === "" || candidate.id.trim() === "") {
            throw new TypeError("Direct transport candidates must have a path, address, and id");
        }
        const address = canonicalHttpCandidateOrigin(candidate.address);
        const identityBinding = candidate.path === "lan-webtransport" ? undefined : candidate.identityBinding;
        if (identityBinding !== "bootstrap")
            trustedNodeIds.add(candidate.id);
        const key = JSON.stringify([candidate.path, address, candidate.id]);
        if (seen.has(key))
            continue;
        seen.add(key);
        tiers[candidate.path].push({ ...candidate, address });
    }
    if (trustedNodeIds.size > 1) {
        throw new TypeError("Direct transport candidates must share one trusted home-node identity");
    }
    return DIRECT_TIER_ORDER.flatMap((path) => tiers[path]);
}
/**
 * Canonicalize a bare HTTPS origin. Named for its HTTP origin, but this rule
 * — exact `https://` scheme, no credentials, no path/query/fragment — is the
 * same origin shape a WebTransport candidate's `address` uses too (ticket
 * #1823): the client-RPC path is appended when dialing, not advertised.
 */
export function canonicalHttpCandidateOrigin(value) {
    let url;
    try {
        url = new URL(value);
    }
    catch {
        throw new TypeError("Direct transport candidates must use an exact HTTPS origin");
    }
    if (url.protocol !== "https:" ||
        url.username !== "" ||
        url.password !== "" ||
        url.hostname === "" ||
        url.origin !== value) {
        throw new TypeError("Direct transport candidates must use an exact HTTPS origin");
    }
    return url.origin;
}
function isDirectCandidate(candidate) {
    return candidate.path === "lan-direct" || candidate.path === "lan-webtransport" || candidate.path === "wan-direct";
}
function priority(path) {
    return DIRECT_TIER_ORDER.indexOf(path);
}
function messageFor(error) {
    return error instanceof Error ? error.message : "unavailable";
}
async function closeClient(client) {
    if (client.close === undefined)
        return;
    await Promise.resolve(client.close()).catch(() => undefined);
}
function rejectedOnAbort(signal) {
    if (signal.aborted)
        return Promise.reject(new Error("transport selection aborted"));
    return new Promise((_resolve, reject) => {
        signal.addEventListener("abort", () => reject(new Error("transport selection aborted")), { once: true });
    });
}
function throwIfAborted(signal) {
    if (signal.aborted)
        throw new Error("transport selection aborted");
}
//# sourceMappingURL=transport-manager.js.map